import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-black text-gray-300">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-2">Terms of Service</h1>
        <p className="text-gray-500 mb-12">Last updated: September 14, 2026</p>

        <div className="space-y-10 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Romulus ("the Service") at{' '}
              <span className="text-white">romulus.app</span>, you agree to be bound by these Terms
              of Service ("Terms"). If you do not agree, do not use the Service.
            </p>
            <p className="mt-3">
              These Terms constitute a legally binding agreement between you and Romulus, operated
              by UnlEESHed Studios ("we," "us," or "our"). We may update these Terms at any time.
              Continued use after changes constitutes acceptance. Material changes will be
              communicated via in-app notice or by updating the "Last updated" date on this page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Eligibility</h2>
            <p>
              You must be at least <span className="text-white font-semibold">18 years old</span> to
              use the Service. By creating an account, you represent and warrant that you are at
              least 18 years of age and have the legal capacity to enter into these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. User Representations</h2>
            <p>By using the Service, you represent and warrant that:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>All information you provide during registration is true, accurate, and complete</li>
              <li>You will maintain the accuracy of such information and update it as necessary</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You are at least 18 years of age</li>
              <li>You will not access the Service through automated or non-human means (bots, scripts, scrapers) unless explicitly permitted</li>
              <li>You will not use the Service for any illegal or unauthorized purpose</li>
              <li>Your use of the Service will not violate any applicable law or regulation</li>
            </ul>
            <p className="mt-3">
              If you provide any information that is untrue, inaccurate, or incomplete, we may
              suspend or terminate your account and refuse any current or future use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Description of Service</h2>
            <p>
              Romulus is a platform that enables users to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Discover local businesses without websites using Google Places data</li>
              <li>Generate AI-powered websites for those businesses</li>
              <li>Conduct outreach to businesses via SMS and email templates</li>
              <li>Sell generated websites to businesses and receive payments via Stripe Connect</li>
            </ul>
            <p className="mt-3">
              Romulus is a <span className="text-white font-semibold">tool</span>. How you use it is
              your responsibility. We provide the platform; you are responsible for your conduct and
              compliance with all applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Service Availability</h2>
            <p>
              We may modify, update, suspend, or discontinue any part of the Service at any time,
              with or without notice. We are not liable for any downtime, interruptions, data loss,
              or changes to features. We do not guarantee that the Service will be available at all
              times or in all locations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Account Registration</h2>
            <p>You agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activity under your account</li>
            </ul>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Subscriptions and Payment</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">7.1 Subscription Plans</h3>
            <p>
              The Service is offered on a monthly subscription basis. Plan details, pricing, and
              quotas are displayed on the pricing section of our website. We reserve the right to
              change pricing with 30 days' notice.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">7.2 Billing</h3>
            <p>
              All payments are processed by Stripe. By subscribing, you authorize recurring monthly
              charges to your payment method. Your subscription renews automatically unless canceled.
              Quotas (leads and sites) reset monthly on your billing date.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">7.3 Cancellation Policy</h3>
            <div className="p-4 rounded-lg border border-white/10 bg-white/5 mt-2">
              <p>
                All payments are non-refundable, except where a refund is required by law.
              </p>
              <p className="mt-2">
                You may cancel your subscription at any time through your account settings or the
                Stripe billing portal. Upon cancellation:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
                <li>Your access continues until the end of the current billing period</li>
                <li>No further charges will be made</li>
                <li>Your data remains accessible until you delete your account</li>
              </ul>
            </div>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">7.4 Site Purchases</h3>
            <p>
              When a business purchases a website generated through the Service, that transaction is
              final. No refunds are provided for completed site purchases. Disputes regarding site
              purchases should be directed to{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Stripe Connect and Payouts</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">8.1 Seller Payouts</h3>
            <p>
              Users who sell websites through the Service receive payouts via Stripe Connect. You
              retain 100% of the sale price you set. Stripe's standard processing fees apply and are
              deducted by Stripe. By using Stripe Connect, you agree to{' '}
              <a
                href="https://stripe.com/connect-account/legal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 underline"
              >
                Stripe's Connected Account Agreement
              </a>.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">8.2 Tax Responsibility</h3>
            <div className="p-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 mt-2">
              <p className="text-yellow-300 font-semibold">Important Tax Notice</p>
              <p className="mt-2">
                <span className="text-white font-semibold">
                  You are solely responsible for determining and fulfilling your tax obligations.
                </span>{' '}
                Income earned through the Service, including revenue from website sales, may be
                subject to federal, state, local, and/or international taxes. Romulus does not
                provide tax advice, does not withhold taxes on your behalf, and is not responsible
                for your tax compliance. You may receive tax forms (e.g., 1099-K) from Stripe if
                you meet applicable thresholds. We strongly recommend consulting a qualified tax
                professional.
              </p>
            </div>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">8.3 Earnings Disclaimer</h3>
            <div className="p-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 mt-2">
              <p className="text-yellow-300 font-semibold">No Guarantee of Earnings</p>
              <p className="mt-2">
                <span className="text-white font-semibold">
                  Any income figures, target numbers, growth curves, projections, or earnings
                  examples shown anywhere on the Romulus website, in onboarding flows, in marketing
                  materials, or inside the product are illustrative goals, not promises or
                  guarantees of future earnings.
                </span>{' '}
                This specifically includes personalized projections generated during the mobile
                onboarding funnel (such as the "target monthly income," "12-month growth goal," and
                "money in vs money out" visualizations), which are based solely on your own answers
                to questions about hours, pricing, and goals. They are not derived from typical
                user data or substantiated with historical performance, and you should not rely on
                them as predictive.
              </p>
              <p className="mt-3">
                Actual results vary significantly between users and depend entirely on factors
                outside our control, including your effort, consistency, skill level, local market
                conditions, competition, pricing strategy, quality of outreach, and sales
                execution. Many users who subscribe to Romulus will not earn the amounts shown in
                projections. Some users may earn nothing at all, and some may lose money after
                accounting for subscription fees, tax obligations, and time invested. Romulus is a
                software tool that helps you generate websites and outreach material. It is not a
                business opportunity, an income product, a get-rich-quick scheme, or a
                guaranteed-return investment.
              </p>
              <p className="mt-3">
                By subscribing to Romulus, you acknowledge that you have read and understood this
                Earnings Disclaimer, that you are making your purchase decision based on your own
                independent judgment, and that you are not relying on any income figures or
                projections shown to you as representations of what you personally will earn.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. User Conduct and Outreach Compliance</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">9.1 Acceptable Use</h3>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Send spam, unsolicited bulk messages, or harassing communications</li>
              <li>Misrepresent your identity or affiliation</li>
              <li>Scrape, crawl, or extract data from the Service beyond its intended use</li>
              <li>Interfere with or disrupt the Service's infrastructure</li>
              <li>Resell or redistribute access to the Service</li>
              <li>Use the Service to generate content that is illegal, defamatory, or infringing</li>
              <li>Circumvent subscription quotas or usage limits</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">9.2 Outreach Compliance</h3>
            <div className="p-4 rounded-lg border border-white/10 bg-white/5 mt-2">
              <p className="text-white font-semibold mb-3">
                You are solely responsible for complying with all applicable outreach and
                communication laws when using our SMS and email tools.
              </p>
              <p>This includes but is not limited to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
                <li>
                  <span className="text-white">CAN-SPAM Act (US):</span> All commercial emails must
                  include accurate sender information, a valid physical mailing address, clear
                  identification as advertising, and a functioning unsubscribe mechanism. You must
                  honor opt-out requests within 10 business days.
                </li>
                <li>
                  <span className="text-white">TCPA (US):</span> Text messages and automated calls
                  to mobile phones require prior express written consent from the recipient. You
                  must maintain records of consent and provide an opt-out mechanism.
                </li>
                <li>
                  <span className="text-white">GDPR (EU/UK):</span> Marketing communications
                  require explicit opt-in consent. Recipients must be able to withdraw consent
                  easily.
                </li>
                <li>
                  <span className="text-white">CASL (Canada):</span> Commercial electronic messages
                  require express or implied consent, sender identification, and an unsubscribe
                  mechanism.
                </li>
                <li>
                  <span className="text-white">Spam Act 2003 (Australia):</span> Unsolicited
                  commercial messages are prohibited without consent.
                </li>
              </ul>
              <p className="mt-3">
                <span className="text-white font-semibold">
                  Romulus provides outreach tools but does not send messages on your behalf.
                </span>{' '}
                You initiate all outreach. Romulus is not responsible for the content, timing,
                frequency, or legality of messages you send using our tools. Violation of outreach
                laws may result in immediate account termination and may expose you to legal
                liability.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. AI-Generated Content</h2>
            <div className="p-4 rounded-lg border border-white/10 bg-white/5">
              <p>
                The Service uses artificial intelligence (Google Gemini and Anthropic Claude) to
                generate website content, including text, descriptions, and service listings.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                <li>
                  <span className="text-white font-semibold">AI content may be inaccurate.</span>{' '}
                  Generated text may contain factual errors, fabricated information, outdated
                  details, or inappropriate content.
                </li>
                <li>
                  <span className="text-white font-semibold">You must review all content.</span>{' '}
                  Before publishing or sharing any generated website, you are responsible for
                  verifying the accuracy and appropriateness of all content.
                </li>
                <li>
                  <span className="text-white font-semibold">No warranty of accuracy.</span>{' '}
                  Romulus makes no representations or warranties regarding the accuracy,
                  completeness, or suitability of AI-generated content.
                </li>
                <li>
                  <span className="text-white font-semibold">Liability disclaimer.</span>{' '}
                  Romulus is not liable for any claims, damages, or losses arising from the use,
                  publication, or distribution of AI-generated content, including but not limited to
                  defamation claims, intellectual property infringement, or misrepresentation.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Google Places Data</h2>
            <div className="p-4 rounded-lg border border-white/10 bg-white/5">
              <p>
                The Service uses the Google Places API to retrieve publicly available business
                information for lead generation.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                <li>
                  <span className="text-white">Data source:</span> Business data (names, addresses,
                  phone numbers, ratings, reviews, photos, hours) is sourced from Google and
                  subject to{' '}
                  <a
                    href="https://cloud.google.com/maps-platform/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-400 hover:text-primary-300 underline"
                  >
                    Google Maps Platform Terms
                  </a>.
                </li>
                <li>
                  <span className="text-white">No guarantee:</span> We do not guarantee the
                  accuracy, completeness, or timeliness of business data retrieved from Google.
                </li>
                <li>
                  <span className="text-white">Photos:</span> Business photos from Google Places
                  may be subject to copyright and usage restrictions. You use them at your own risk
                  and are responsible for ensuring proper rights or attribution.
                </li>
                <li>
                  <span className="text-white">Not endorsed by Google:</span> Romulus is not
                  endorsed by, affiliated with, or sponsored by Google.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Intellectual Property</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.1 Our Property</h3>
            <p>
              The Service, including its source code, design, functionality, and original content,
              is the property of Romulus. You are granted a limited, non-exclusive, non-transferable,
              revocable license to use the Service for its intended purpose. You may not copy,
              modify, distribute, reverse engineer, or create derivative works from any part of
              the Service.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.2 Your Content</h3>
            <p>
              You retain ownership of any original content you create using the Service (e.g.,
              custom text, branding materials). By creating or uploading content through the Service,
              you grant Romulus a non-exclusive, worldwide, royalty-free license to use, host, store,
              reproduce, modify, and display such content solely for the purpose of operating and
              providing the Service. For AI-generated content, you are granted a non-exclusive,
              worldwide license to use, modify, and distribute the generated content for your
              business purposes.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.3 Third-Party Content</h3>
            <p>
              Google reviews, business photos, and other third-party content remain the property of
              their respective owners. Your use of such content is subject to the applicable
              third-party terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Copyright Infringement</h2>
            <p>
              We respect the intellectual property rights of others. If you believe that any content
              on the Service infringes upon a copyright you own, please notify us at{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>{' '}
              with: (a) a description of the copyrighted work; (b) the location of the infringing
              material on the Service; (c) your contact information; (d) a statement that you have a
              good faith belief the use is not authorized; and (e) a statement under penalty of
              perjury that your notice is accurate and you are the copyright owner or authorized to
              act on their behalf. We will review and respond to valid notices promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">14. Disclaimers</h2>
            <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/5">
              <p className="uppercase text-sm text-red-300 font-semibold mb-3">Important</p>
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="mt-3">
                ROMULUS DOES NOT WARRANT THAT: (A) THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE;
                (B) AI-GENERATED CONTENT WILL BE ACCURATE, COMPLETE, OR SUITABLE; (C) BUSINESS DATA
                FROM GOOGLE PLACES WILL BE CURRENT OR ACCURATE; (D) THE SERVICE WILL MEET YOUR
                SPECIFIC REQUIREMENTS; OR (E) ANY RESULTS OBTAINED FROM USING THE SERVICE WILL BE
                RELIABLE.
              </p>
              <p className="mt-3">
                YOU USE THE SERVICE AT YOUR OWN RISK. ROMULUS IS NOT RESPONSIBLE FOR ANY DAMAGES
                ARISING FROM ACTIONS YOU TAKE BASED ON DATA OR CONTENT PROVIDED BY THE SERVICE,
                INCLUDING OUTREACH CONDUCTED USING OUR TOOLS.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">15. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ROMULUS SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
              LOSS OF PROFITS, DATA, BUSINESS, OR GOODWILL, ARISING FROM OR RELATED TO YOUR USE OF
              THE SERVICE.
            </p>
            <p className="mt-3">
              OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE
              SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
            <p className="mt-3">
              This limitation does not apply to liability that cannot be excluded or limited under
              applicable law, including mandatory consumer protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">16. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Romulus and its officers, employees,
              and agents from any claims, damages, losses, liabilities, and expenses (including
              reasonable attorneys' fees) arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Your use of the Service</li>
              <li>Your outreach activities, including any messages sent using our tools</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable law or third-party rights</li>
              <li>Content you create, modify, publish, or distribute using the Service</li>
              <li>Your use of AI-generated content without proper review</li>
              <li>Your use of Google Places data or photos</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">17. User Data</h2>
            <p>
              You are solely responsible for all data that you transmit or that relates to any
              activity you have undertaken using the Service. Although your data is stored on
              Firebase (Google Cloud) infrastructure, we do not guarantee that your data will not
              be lost or corrupted. You agree that we shall have no liability for any loss or
              corruption of your data, and you waive any right of action against us arising from
              any such loss or corruption.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">18. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause, including for violation of these Terms. Upon termination:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Your right to use the Service ceases immediately</li>
              <li>We may delete your data in accordance with our Privacy Policy</li>
              <li>No refund will be issued for any remaining subscription period</li>
              <li>Provisions that should survive termination (disclaimers, limitation of liability, indemnification) will remain in effect</li>
            </ul>
            <p className="mt-3">
              You may terminate your account at any time by deleting it from your Settings page or
              by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">19. Electronic Communications</h2>
            <p>
              By using the Service, you consent to receive electronic communications from us
              (e.g., emails, in-app notifications). You agree that all agreements, notices,
              disclosures, and other communications we provide electronically satisfy any legal
              requirement that such communications be in writing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">20. Data Breach Notice</h2>
            <p>
              If a security breach occurs that affects your personal information, we will notify
              affected users as required by applicable law. Notification will be provided via
              in-app notice or other available contact methods.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">21. Corrections</h2>
            <p>
              There may be information on the Service that contains typographical errors,
              inaccuracies, or omissions, including pricing, descriptions, and availability. We
              reserve the right to correct any errors and to update information at any time without
              prior notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">22. Dispute Resolution</h2>
            <p>
              Any disputes arising from these Terms or the Service shall be resolved as follows:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>
                <span className="text-white">Informal resolution:</span> You agree to first contact
                us at{' '}
                <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                  unleeshedstudios@gmail.com
                </a>{' '}
                and attempt to resolve the dispute informally for at least 30 days.
              </li>
              <li>
                <span className="text-white">Governing law:</span> These Terms are governed by the
                laws of the State of Florida, United States, without regard to conflict of law
                principles.
              </li>
              <li>
                <span className="text-white">Jurisdiction:</span> Any legal action shall be brought
                exclusively in the state or federal courts located in the State of Florida.
              </li>
              <li>
                <span className="text-white">Binding arbitration (US residents):</span> If informal
                resolution fails, disputes will be resolved by binding arbitration administered under
                the AAA Consumer Arbitration Rules. Arbitration will be conducted on an individual
                basis in the State of Florida. The arbitrator's decision is final and binding.
              </li>
              <li>
                <span className="text-white">Class action waiver:</span> YOU AGREE THAT ANY DISPUTE
                RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A
                CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. You waive the right to participate in
                class action lawsuits or class-wide arbitration.
              </li>
              <li>
                <span className="text-white">Small claims exception:</span> Either party may bring
                an individual action in small claims court if the claim qualifies.
              </li>
              <li>
                <span className="text-white">International users:</span> If you reside outside the
                United States, this arbitration provision may not apply to you. Nothing in these
                Terms limits your rights under mandatory consumer protection laws of your country of
                residence. You may have the right to bring claims in your local courts.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">23. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that
              provision shall be limited or eliminated to the minimum extent necessary, and the
              remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">24. Miscellaneous</h2>
            <p>
              These Terms and any policies posted on the Service constitute the entire agreement
              between you and us. Our failure to exercise or enforce any right or provision of these
              Terms shall not operate as a waiver of such right or provision. We may assign any or
              all of our rights and obligations under these Terms to others at any time. We shall
              not be responsible or liable for any loss, damage, delay, or failure to act caused by
              any cause beyond our reasonable control (force majeure), including but not limited to
              natural disasters, acts of government, internet outages, or third-party service
              failures. No joint venture, partnership, employment, or agency relationship is created
              between you and us as a result of these Terms or your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">25. Entire Agreement</h2>
            <p>
              These Terms, together with our{' '}
              <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">
                Privacy Policy
              </Link>
              , constitute the entire agreement between you and Romulus regarding your use of the
              Service and supersede all prior agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">26. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <p className="mt-3">
              <span className="text-white">Romulus</span><br />
              Operated by UnlEESHed Studios<br />
              Atlanta, GA, United States<br />
              Email:{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between text-sm text-gray-500">
          <span>&copy; {new Date().getFullYear()} Romulus. All rights reserved.</span>
          <Link to="/privacy" className="text-primary-400 hover:text-primary-300 transition-colors">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
